<template>
  <v-container>
    <div v-if="alertLoader" class="mb-3">
      <!-- Alerty pro admin sdělení -->
      <v-alert
        v-if="showPanelMessageText && panelMessageText"
        title=""
        :text="panelMessageText"
        variant="tonal"
        closable
        @click:close="handleClose('panelMessageText')"
      ></v-alert>

      <v-alert
        type="info"
        v-if="showPanelMessageInfo && panelMessageInfo"
        :text="panelMessageInfo"
        variant="tonal"
        closable
        @click:close="handleClose('panelMessageInfo')"
      ></v-alert>

      <v-alert
        type="warning"
        v-if="showPanelMessageWarning && panelMessageWarning"
        :text="panelMessageWarning"
        variant="tonal"
        closable
        @click:close="handleClose('panelMessageWarning')"
      ></v-alert>
    </div>

    <!-- Alert pro zobrazení zprávy -->
    <v-alert type="error" v-if="playgroundUrl" class="mb-2">Toto je testovací server. Jeho databáze není spojená s oficiálním serverem. <a href="https://docs.google.com/spreadsheets/d/1JimmEcBN3jaOYIxKDr5WNi-SDgccxBU_8P0yXMuQxNQ/edit?usp=sharing" target="_blank">Report o chybách a vylepšení</a>.</v-alert>

      <div class="d-flex justify-space-between">
        <router-link v-if="isOrganizer" to="/soutez/vytvorit">
          <v-icon color="green" size="40" :title="$t('home.create_event')">mdi-calendar-plus</v-icon>
        </router-link>
        <span> </span>
        <div class="d-flex" v-if="currentCalendar === 'table'">
        <v-select 
          :items="[{ title: $t('home.current'), key: 'current' }, { title: $t('home.all'), key: 'all' }]"
          item-text="title"
          item-value="key"
          v-model="selectedPeriod" 
          density="compact" 
          :label="$t('home.Period')" 
          hide-details="true" 
          variant="outlined"
          style="width: 130px;" 
          @change="fetchEvents(selectedYear, selectedPeriod)"
        >
        </v-select>
        <span style="width:10px"></span>
        <SelectYear 
          v-model="selectedYear" 
          style="width: 100px;" 
          @change="fetchEvents(selectedYear, selectedPeriod)" 
          density="compact" 
          variant="outlined"
          :label="$t('home.year')"  
        />
      </div>
    </div>

    <h2>{{ $t('home.calendar') }}:</h2>

    <div v-if="currentCalendar === 'graphical'">

      <GraphicalCalendar :currentCalendar="currentCalendar" />

    </div>


<!-- Skeleton Loader pro tabulku, zobrazí se pouze při načítání -->
    <v-data-table
      density="comfortable"
      :headers="eventHeaders"
      :items="events"
      item-key="id"
      :items-per-page="-1"
      style="border-bottom: thin solid #808080"
      :sort-by="sortBy"
      :row-props="ColorRowProps" 
      v-model:expanded="expanded"
      show-expand
      :loading="loading"
      loading-text="Načítám data..."
      ref="eventTableRef" 
      hover
      v-else
    >

      <!-- Skeleton Loader -->
      <template v-slot:loading>
        <div v-for="n in 6" :key="n" class="skeleton-row d-flex my-4">
          <v-skeleton-loader class="skeleton-cell mr-2" width="11%" height="24px"></v-skeleton-loader>
          <v-skeleton-loader class="skeleton-cell mr-2" width="38%" height="24px"></v-skeleton-loader> 
          <v-skeleton-loader class="skeleton-cell mr-2" width="3%" height="24px"></v-skeleton-loader> 
          <v-skeleton-loader class="skeleton-cell mr-2" width="28%" height="24px"></v-skeleton-loader> 
          <v-skeleton-loader class="skeleton-cell mr-2" width="6%" height="24px"></v-skeleton-loader> 
          <v-skeleton-loader class="skeleton-cell mr-2" width="3%" height="24px"></v-skeleton-loader> 
          <v-skeleton-loader class="skeleton-cell mr-2" width="8%" height="24px"></v-skeleton-loader> 
        </div>
      </template>

      <template v-slot:no-data>
        <v-alert type="info" class="ma-4">
          {{ $t('home.no_event') }}
        </v-alert>
      </template>

      <template v-slot:[`body.prepend`]>
        <tr
          v-if="showExpandPeriodUp"
          class="calendar-expand-period-row"
          @click="showFullYear"
        >
          <td :colspan="eventHeaders.length" class="text-center calendar-expand-period-cell">
            <v-icon size="18">mdi-chevron-up</v-icon>
          </td>
        </tr>
      </template>

      <template v-slot:[`body.append`]>
        <tr
          v-if="showExpandPeriodDown"
          class="calendar-expand-period-row"
          @click="showFullYear"
        >
          <td :colspan="eventHeaders.length" class="text-center calendar-expand-period-cell">
            <v-icon size="18">mdi-chevron-down</v-icon>
          </td>
        </tr>
      </template>
      
      <!-- Datum eventu -->
      <template v-slot:[`item.event_date_start`]="{ item }">
        <div style="text-align: left;" :class="{'canceled-event': item.canceled}"> <!-- Zarovnání obsahu buňky vlevo -->
          <span>{{ $formatDate(item.event_date_start) }}</span>
          <span v-if="item.event_date_end && item.event_date_end !== item.event_date_start" style="white-space: nowrap;">
            &ndash; {{ $formatDate(item.event_date_end) }}
          </span>        
        </div>
      </template>

      <!-- Název eventu -->
      <template v-slot:[`item.event_name`]="{ item }">
        <div style="text-align: left;" :class="{'canceled-event': item.canceled }">
          <span v-html="levelPrefix(item)"  class="text-caption font-italic"></span>
          <router-link :to="'/soutez/' + item.id" class="event-name">{{ item.event_name }}</router-link> 
          <v-icon v-if="item.visibility == 'CLUB'" size="small" class="ml-1">mdi-lock-open</v-icon>
        </div>
      </template>

      <!-- Zobrazí expanzní šipku jen pokud item.races má závody -->
      <template v-slot:[`item.data-table-expand`]="{ item }">
        <v-icon v-if="item.races && item.races.length > 0" @click="toggleExpand(item)">
          {{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </template>


      <!-- Typ eventu -->
      <template v-slot:[`item.event_type`]="{ item }">
        <div style="overflow: hidden; white-space: nowrap; text-align: left;" :class="{'canceled-event': item.canceled}"> 
          {{ EVENT_TYPE_DICT[item.event_type] }}
        </div>
      </template>

      <!-- Klub -->      
      <template v-slot:[`item.club_shortcut`]="{ item }">
        <div style="overflow: hidden; white-space: nowrap; text-align: center;" :class="{'canceled-event': item.canceled}"> 
          {{ item.club_shortcut }}
        </div>
      </template>


      <!-- Počet přihlášek -->
      <template v-slot:[`item.entries`]="{ item }">
        <div style="overflow: hidden; white-space: nowrap;" :class="{'canceled-event': item.canceled}">
          {{ item.entries }}
        </div>
      </template>

      <!-- Odkazy (propozice, pokyny, startovka, výsledky) -->
      <template v-slot:[`item.links`]="{ item }">
        <div style="text-align: left; white-space: nowrap;">
          <span class="me-1">
            <router-link :to="`/soutez/${item.id}/prihlaska/`" v-if="item.entry_is_allow">
              <v-icon :color="item.entry_deadline_is_second ? 'orange-accent-4' : 'green'" size="x-large" :title="$t('home.create_entry')">mdi-account-plus</v-icon>
            </router-link> 
            <sup v-if="item.entry_deadline && item.entry_is_allow"
                :class="{ 'text-orange-darken-4': item.entry_deadline_is_second }">
              {{ $formatShortDate(item.entry_deadline) }}
            </sup>
          </span>
          <a v-if="item.bulletin" :href="`${BaseUrl}${item.bulletin}`" target="_blank">
            <v-icon color="primary" size="x-large" :title="$t('home.bulletin')">mdi-text-box</v-icon>
          </a>
          <a v-if="item.info" :href="`${BaseUrl}${item.info}`" target="_blank">
            <v-icon color="primary" size="x-large" :title="$t('home.info')">mdi-information-slab-box</v-icon>
          </a>
          <a v-if="item.startlist" :href="`${BaseUrl}${item.startlist}`" target="_blank">
            <v-icon color="primary" size="x-large" :title="$t('home.startlist')">mdi-timer-play</v-icon>
          </a>
          <router-link
            v-if="isLiveRace(item.first_race_id)"
            :to="liveResultsPath(item.first_race_id)"
          >
            <v-icon color="primary" size="x-large" :title="$t('home.results')">mdi-trophy</v-icon>
          </router-link>
          <a v-else-if="item.results" :href="`${BaseUrl}${item.results}`" target="_blank">
            <v-icon color="primary" size="x-large" :title="$t('home.results')">mdi-trophy</v-icon>
          </a>
        </div>
      </template>
      

      <!-- Zobrazení závodů uvnitř eventu -->
      <template  v-slot:expanded-row="{ columns, item }">
        <td :colspan="columns.length">
          <v-data-table
            density="compact"
            :headers="raceHeaders"
            :items="sortedRaces(item.races)" 
            item-key="id"
            hide-default-footer
            hide-default-header
            hide-no-data
            :items-per-page="-1"
            style="border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity)); color:#808080;"
          >
            <template v-slot:[`item.race_name`]="{ item: race }">
              <div :class="{'canceled-event': item.canceled}" style="text-align: left;">
                {{ race.race_name }}
              </div>
            </template>

            <template v-slot:[`item.race_date`]="{ item: race }">
              <div :class="{'canceled-event': item.canceled}" style="text-align: right;">
                {{ $formatDate(race.race_date) }}
              </div>
            </template>

            <template v-slot:[`item.race_level`]="{ item: race }">
              <div :class="{'canceled-event': item.canceled}" style="text-align: left;">
                {{ RACE_LEVEL_DICT[race.race_level] }}
              </div>
            </template>

            <template v-slot:[`item.entries`]="{ item: race }">
              <div style="text-align: center;">
                {{ race.entries }}
              </div>
            </template>

            <template v-slot:[`item.links`]="{ item }">
              <div style="text-align: left;">
                <a v-if="item.bulletin" :href="`${BaseUrl}${item.bulletin}`" target="_blank">
                  <v-icon color="primary" size="x-large" title="propozice">mdi-text-box</v-icon>
                </a>
                <a v-if="item.info" :href="`${BaseUrl}${item.info}`" target="_blank">
                  <v-icon color="primary" size="x-large" title="pokyny">mdi-information-slab-box</v-icon>
                </a>
                <a v-if="item.startlist" :href="`${BaseUrl}${item.startlist}`" target="_blank">
                  <v-icon color="primary" size="x-large" title="startovka">mdi-timer-play</v-icon>
                </a>
                <router-link
                  v-if="isLiveRace(item.id)"
                  :to="liveResultsPath(item.id)"
                >
                  <v-icon color="primary" size="x-large" title="výsledky">mdi-trophy</v-icon>
                </router-link>
                <a v-else-if="item.results" :href="`${BaseUrl}${item.results}`" target="_blank">
                  <v-icon color="primary" size="x-large" title="výsledky">mdi-trophy</v-icon>
                </a>
              </div>
            </template>
          </v-data-table>
        </td>
      </template>
      <template v-slot:bottom></template>
    </v-data-table>

    <!-- Baner pro zobrazení souhlasu s využitím utils třetích stran -->
    <v-banner v-if="showCookieBanner" rounded="lg" color="warning" icon="mdi-shield-check" style="max-width: 1100px;" class="ma-auto mt-3 d-flex align-center justify-center elevation-2">
      <v-banner-text>
        {{ $t('home.cookies') }}.
      </v-banner-text>
      <v-banner-actions class="ma-auto d-flex">
        <v-btn @click="acceptCookies">{{ $t('home.agree') }}</v-btn>
      </v-banner-actions>
    </v-banner>

    <div class="d-flex justify-center" v-if="!loading">
      <v-card
        class="mt-3"
        max-width="600"
        href="https://github.com/kolskypavel/Radio-O-Manager/wiki/Zaciname"
        target="_blank"
        rel="noopener"
        variant="text"
      >
        <v-img :src="`${BaseUrl}/static/ARDF_manager_banner.png`" width="600px" cover eager />
      </v-card>
    </div>


    
  </v-container>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
// import { useDate } from 'vuetify'
import apiClient from '/api.js';
import { useAuthStore } from '/auth.js';
import SelectYear from '@/components/shared/SelectYear.vue'; 
import GraphicalCalendar from '@/components/HomePageGraphicCalendar.vue';
import {levelPrefix} from '@/utils/raceLevelCalendar.js';
import { useI18n } from 'vue-i18n'
import { EVENT_TYPE_DICT, RACE_LEVEL_DICT } from '@/components/const/enums.js'

const { t } = useI18n()
const authStore = useAuthStore();
const loading = ref(true); 
const events = ref([]);

const selectedPeriod = ref((process.env.NODE_ENV === 'development' || window.location.hostname === 'playground.rob-is.cz') ? 'all' : 'current');
const selectedYear = ref(new Date().getFullYear());
const playgroundUrl = ref(false);

const sortBy = [{ key: 'event_date_start', order: 'asc' }]; // Klíč pro řazení

const currentCalendar = computed(() => authStore.calendarType)


const eventHeaders = computed(() => [
  { title: t('home.date'),        key: 'event_date_start',  align: 'center', sortable: true, width: 182 },
  { title: t('home.name'),        key: 'event_name',        align: 'center', minWidth: 200 },
  { title: '',                    key: 'data-table-expand', align: 'center', width: 50 },
  { title: t('home.type'),        key: 'event_type',align: 'center', width: 250, maxWidth: 250 },
  { title: t('home.organizer'),   key: 'club_shortcut',     align: 'center', width: 90 },
  { title: t('home.entries'),     key: 'entries',           align: 'center', width: 50,  sortable: false },
  { title: t('home.link'),        key: 'links',             align: 'center', width: 160, sortable: false },
])


const raceHeaders = ref([]);

const expanded = ref([]); // Uchovává expandované řádky

const isExpanded = (item) => {
  return expanded.value.includes(item.id); // Kontrola podle ID
};

const toggleExpand = (item) => {
  if (isExpanded(item)) {
    expanded.value = expanded.value.filter(id => id !== item.id); // Odstranit ID z expandovaných
  } else {
    expanded.value.push(item.id); // Přidat ID do expandovaných
  }
};

// Rozevření races v eventu u blížících se akcí a těsně skončených
const isWithinTimeFrame = (eventDateStart) => {
  const now = new Date();
  const eventDate = new Date(eventDateStart);

  const timeBefore = new Date(now);
  timeBefore.setDate(now.getDate() - 6); // 6 dnů po konci

  const timeAfter = new Date(now);
  timeAfter.setDate(now.getDate() + 2); // 2 dny před začátkem

  // Podmínka, pokud je event_date_start méně než 1 měsíc před nebo méně než 1 měsíc po dnešním datu
  return eventDate >= timeBefore && eventDate <= timeAfter;
};


////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
/////////////////     PANEL PRO ZPRÁVY     /////////////////////////////
////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////



const alertLoader = ref(false);

const panelMessageText = ref('');
const panelMessageInfo = ref('');
const panelMessageWarning = ref('');
const showPanelMessageText = ref(true);
const showPanelMessageInfo = ref(true);
const showPanelMessageWarning = ref(true);

const fetchAdminPanelMessages = async () => {
  try {
    const response = await apiClient.get('/admin_message/');
    if (response.data && response.data.length > 0) {
      const messageData = response.data[0];
      panelMessageText.value = messageData.panel_message_text;
      panelMessageInfo.value = messageData.panel_message_info;
      panelMessageWarning.value = messageData.panel_message_warning;

      alertLoader.value = true;
      checkAlertVisibility();
    }
  } catch (error) {
    console.error('Chyba při načítání zpráv:', error);
  } finally {
    // endpoint načítej po admin message
    fetchLiveResultsStatus();
  }
};

const LIVE_CACHE_MAX_AGE_MS = 30_000; // rychlá UI cache, backend je source of truth

const fetchLiveResultsStatus = async () => {
  // pokud máme relativně čerstvou cache, nech ji do fetch dokončení (LIVE badge hned)
  const updatedAt = authStore.liveResultsUpdatedAt || 0
  if (updatedAt && (Date.now() - updatedAt) < LIVE_CACHE_MAX_AGE_MS) {
    // stále fetchujeme, jen nezhasínej UI
  }

  try {
    const resp = await apiClient.get('/results/live_status/')
    const data = resp?.data || {}
    authStore.setLiveResults(!!data.is_live, {
      raceIds: data.race_ids ?? [],
    })
  } catch (e) {
    // pokud endpoint spadne, necháme poslední cache hodnotu
    console.warn('Live results status fetch failed', e)
  }
}

const isLiveRace = (raceId) =>
  authStore.liveResultsActive
  && raceId != null
  && authStore.liveResultsRaceIds.includes(Number(raceId))

const liveResultsPath = (raceId) =>
  `/vysledky/${new Date().getFullYear()}/${raceId}`

// Kontrola viditelnosti podle času uložení
const checkAlertVisibility = () => {
  const now = new Date().getTime();
  const dayInMs = 24 * 60 * 60 * 1000;

  if (localStorage.getItem('panelMessageTextClosedAt')) {
    const closedAt = parseInt(localStorage.getItem('panelMessageTextClosedAt'));
    showPanelMessageText.value = now - closedAt > dayInMs;
  }

  if (localStorage.getItem('panelMessageInfoClosedAt')) {
    const closedAt = parseInt(localStorage.getItem('panelMessageInfoClosedAt'));
    showPanelMessageInfo.value = now - closedAt > dayInMs;
  }

  if (localStorage.getItem('panelMessageWarningClosedAt')) {
    const closedAt = parseInt(localStorage.getItem('panelMessageWarningClosedAt'));
    showPanelMessageWarning.value = now - closedAt > dayInMs;
  }
};

// Funkce pro zavření upozornění a uložení času do localStorage
const handleClose = (alertType) => {
  const now = new Date().getTime();
  localStorage.setItem(`${alertType}ClosedAt`, now);

  if (alertType === 'panelMessageText') {
    showPanelMessageText.value = false;
  } else if (alertType === 'panelMessageInfo') {
    showPanelMessageInfo.value = false;
  } else if (alertType === 'panelMessageWarning') {
    showPanelMessageWarning.value = false;
  }
};


////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////


// Oprávnění pro organizátora k vytváření soutěží
const isOrganizer = computed(() => authStore.isOrganizer())


const BaseUrl = ref(process.env.VUE_APP_API_BASE_URL.replace(/\/+$/, '').replace(/\/api\/?$/, '')); // Odstraní "api/" a nadbytečná lomítka

const fetchUserPermissions = async () => {

  if (!authStore.isLoggedIn) {
    console.log('User is not logged in. Skipping permissions fetch.');
    return;
  }

  try {
    const response = await apiClient.get('account/permissions/');
    if (response.status === 200) {
      authStore.updateRoles(response.data); // uložení rolí uživatele do pinia auth sotre
    } else {
      throw new Error('Failed to retrieve permissions');
    }
  } catch (error) {
    console.error('Error retrieving permissions:', error);
  }
};


const fetchEvents = async (year, period) => {
loading.value = true; 
  try {
    const response = await apiClient.get(`/event/?year=${year}&period=${period}`);
    events.value = response.data
      .slice()
      .sort((a, b) => new Date(a.event_date_start) - new Date(b.event_date_start));

  } catch (error) {
    console.error('Error fetching events:', error);
  } finally {
    loading.value = false;
  }

};

const pendingScrollAnchor = ref(null);

// Backend /event/?period=current: today-21 … today+60 (viz kalendar/views.py)
const CURRENT_PERIOD_PAST_DAYS = 21;
const CURRENT_PERIOD_FUTURE_DAYS = 60;

/** Šipka nahoru: jen když před oknem ještě zbývá část vybraného roku. */
const showExpandPeriodUp = computed(() => {
  if (selectedPeriod.value !== 'current' || loading.value) return false;
  const year = Number(selectedYear.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (year !== today.getFullYear()) return false;
  const yearStart = new Date(year, 0, 1);
  const daysFromYearStart = Math.round((today - yearStart) / 86400000);
  return daysFromYearStart > CURRENT_PERIOD_PAST_DAYS;
});

/** Šipka dolů: jen když za oknem ještě zbývá část vybraného roku. */
const showExpandPeriodDown = computed(() => {
  if (selectedPeriod.value !== 'current' || loading.value) return false;
  const year = Number(selectedYear.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (year !== today.getFullYear()) return false;
  const yearEnd = new Date(year, 11, 31);
  const daysToYearEnd = Math.round((yearEnd - today) / 86400000);
  return daysToYearEnd > CURRENT_PERIOD_FUTURE_DAYS;
});

/** Event nejblíž středu obrazovky + jeho offset od horního okraje viewportu. */
const findViewportCenterEvent = () => {
  const midY = window.innerHeight / 2;
  const rows = document.querySelectorAll('tr[data-event-id]');
  let best = null;
  let bestDist = Infinity;

  rows.forEach((row) => {
    const rect = row.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    const center = (rect.top + rect.bottom) / 2;
    const dist = Math.abs(center - midY);
    if (dist < bestDist) {
      bestDist = dist;
      best = {
        id: String(row.getAttribute('data-event-id')),
        offsetFromViewportTop: rect.top,
      };
    }
  });

  return best;
};

const restoreScrollToEvent = (anchor) => {
  if (!anchor?.id) return;
  const row = document.querySelector(`tr[data-event-id="${anchor.id}"]`);
  if (!row) return;
  const rect = row.getBoundingClientRect();
  const delta = rect.top - anchor.offsetFromViewportTop;
  if (Math.abs(delta) < 1) return;
  window.scrollBy({ top: delta, behavior: 'auto' });
};

const showFullYear = () => {
  if (selectedPeriod.value === 'all') return;
  pendingScrollAnchor.value = findViewportCenterEvent();
  selectedPeriod.value = 'all';
};

// sledování změny v parametrech currentCalendar, aby se načetl fetchEvents když se mění typ kalendáře
watch(currentCalendar, (newVal) => {
  if (newVal === 'table') {
    const currentYear = new Date().getFullYear()
    fetchEvents(currentYear, 'current')
  }
})



const sortedRaces = (races) => {
  return races.slice().sort((a, b) => {
    // Seřadit podle race_date
    const dateComparison = new Date(a.race_date) - new Date(b.race_date);
    if (dateComparison !== 0) {
      return dateComparison;
    }
    // Pokud jsou stejné race_date, seřadit podle race_start
    return new Date(a.race_start) - new Date(b.race_start);
  });
};


const eventTableRef = ref(null); 
const totalTitleWidth = ref(0);  // Ref pro celkovou šířku, kterou aplikujeme na druhou tabulku


onMounted(() => {
  if (authStore.calendarType === 'table') {
    fetchEvents(selectedYear.value, selectedPeriod.value); // Načtení událostí při načtení komponenty
  }  
  fetchAdminPanelMessages();
  fetchUserPermissions();

  // updateEvents() pro grafický kalendář
  watch(events, async () => {
    if (authStore.calendarShowMore) { // rozbal, jen když je povoleno
      expanded.value = events.value
        .filter(event => isWithinTimeFrame(event.event_date_start))
        .map(event => event.id);
    } else {
      // zavři všechny rozbalené řádky
      expanded.value = [];
    }

    await nextTick(); // Počká na vykreslení DOMu

    // Získání šířek sloupců po vykreslení tabulky
    getHeaderWidths();

    // Po otevření celého roku: vrať viewport na event, který byl uprostřed
    if (pendingScrollAnchor.value) {
      const anchor = pendingScrollAnchor.value;
      pendingScrollAnchor.value = null;
      await nextTick();
      requestAnimationFrame(() => restoreScrollToEvent(anchor));
    }

  // Pokud cookie neexistuje, zobrazí se banner
  showCookieBanner.value = !document.cookie.includes('cookieConsent=true');

  });

// Sledování změny authStore.calendarShowMore pro zavření rozbalených řádků
watch(
  () => authStore.calendarShowMore,
  (show) => {
    if (show) {
      expanded.value = events.value
        .filter(event => isWithinTimeFrame(event.event_date_start))
        .map(event => event.id)
    } else {
      expanded.value = []
    }
  }
)



  const hostname = window.location.hostname;
  // Podmíněné zobrazení alertu pouze pro specifickou URL
  playgroundUrl.value = (hostname === 'playground.rob-is.cz');
});



const getHeaderWidths = () => {
  nextTick(() => {
    if (!eventTableRef.value || !eventTableRef.value.$el) {
      return; // Element zatím není dostupný (je zapnutý grafický kalendář) – nic neděláme
    }
    const eventTableElement = eventTableRef.value.$el;
    const headerCells = eventTableElement.querySelectorAll('th');

    if (headerCells.length > 0) {
      const header0Width = headerCells[0].offsetWidth;
      const header1Width = headerCells[1].offsetWidth;
      const header2Width = headerCells[2].offsetWidth;
      const header6Width = headerCells[6].offsetWidth;


      totalTitleWidth.value = header1Width + header2Width; // Nastavení dynamické šířky pro title

      // Ošetření, aby se v Chrome nezobrazoval nezarovnaný sloupec o 1 px
      // const isEdge = /Edg/.test(navigator.userAgent);
      // const isChrome = !isEdge && /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

      // if (isEdge) {
      //   totalTitleWidth.value = (header1Width + header2Width) - 1; // Zmenšení o 1 px pro Edge
      // } else if (isChrome) {
      //   totalTitleWidth.value = (header1Width + header2Width) - 1; // Zmenšení o 1 px pro Chrome
      // } else {
      //   totalTitleWidth.value = header1Width + header2Width; // Standardní šířka pro ostatní prohlížeče
      // }

      
      // Aktualizace raceHeaders s novou hodnotou šířky
      raceHeaders.value = [
        { title: 'Datum', key: 'race_date', align: 'center', sortable: true, width: header0Width },
        { title: 'Název závodu', key: 'race_name', align: 'center', width: totalTitleWidth.value }, // Aktualizace width
        { title: 'Typ závodu', key: 'race_level', align: 'center' },
        { title: 'Přihl.', key: 'entries', align: 'center', width: 54 },
        { title: 'Odkazy', key: 'links', align: 'center', width: header6Width, sortable: false }
      ];
    }
  });
};

// Sledování změn v parametrech pro výpis kalendáře
watch(selectedYear, (newYear) => {
  const currentYear = new Date().getFullYear();
  if (parseInt(newYear) !== currentYear && selectedPeriod.value !== "all") {
    selectedPeriod.value = "all";
    return; // selectedPeriod watcher se spustí a zavolá fetchEvents
  }
  fetchEvents(selectedYear.value, selectedPeriod.value);
});

watch(selectedPeriod, (newPeriod) => {
  const currentYear = new Date().getFullYear();
  if (newPeriod === "current" && parseInt(selectedYear.value) !== currentYear) {
    selectedYear.value = currentYear;
    return; // selectedYear watcher se spustí a zavolá fetchEvents
  }
  fetchEvents(selectedYear.value, selectedPeriod.value);
});



// Zvýraznění řádku v kalendáři, pokud je uživatel na event přihlášený
const UserHasEntryHighlight = ({ item }) => {
  return item.user_has_entry ? { class: 'user_has_entry' } : {};
};

// Zvýraznění řádku v kalendáři, pokud má uživatel absence v eventu
const UserHasAbsenceHighlight = ({ item }) => {
  return item.user_has_absence ? { class: 'user_has_absence' } : {};
};


// Zvýraznění řádku v kalendáři, kde se nachází aktuální datum
const borderIndex = computed(() => {
  const today = new Date()
  today.setHours(0,0,0,0)

  // events.value už je seřazené podle data ve fetchEvents
  // najdi index první události, jejíž date_start > dnes
  return events.value.findIndex(e => {
    const d = new Date(e.event_date_start)
    d.setHours(0,0,0,0)
    return d.getTime() > today.getTime()
  })
})

const DateBorderHighlight = ({ index }) =>
  index === borderIndex.value
    ? { class: 'date-border-highlight' }
    : {}


// Wrapper, který obě zvýraznění řádků jejichž definice je výše
const ColorRowProps = (ctx) => {
  const user = UserHasEntryHighlight(ctx)   // { class: 'user_has_entry' } nebo {}
  const abs = UserHasAbsenceHighlight(ctx)   // { class: 'user_has_absence' } nebo {}
  const date = DateBorderHighlight(ctx)     // { class: 'date-border-highlight' } nebo {}

  // vytáhneme oba 'class' (pokud existují) a dáme je do pole
  const classes = [
    abs.class,
    user.class,
    date.class
  ].filter(Boolean) // odstraní undefined / prázdné položky

  return {
    class: classes,
    'data-event-id': ctx.item?.id,
  }
}






// Zobrazení cookie banneru
const showCookieBanner = ref(false);

// Uložení souhlasu do cookies
const acceptCookies = () => {
  document.cookie = "cookieConsent=true; max-age=31536000; path=/"; // Cookie s platností 1 rok
  showCookieBanner.value = false; // Skryje banner
};


</script>




<style scoped>



  :deep() .v-table .v-table__wrapper > table > thead > tr > th:not(:last-child) {
    border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding: 0 10px !important; 

  }
  :deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child), .v-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) { 
    border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding: 0 10px !important; 

  }

  /* :deep() table th + th { border-left:10px solid #dddddd; }
  :deep() table td + td { border-left:10px solid #dddddd; } */




:deep(.date-border-highlight td),
:deep(.date-border-highlight th) {
  border-top: 2px solid orange !important;
}
 

  /* Zvýraznění řádku: barvy z Vuetify theme (light/dark se přepínají automaticky) */
:deep(.user_has_absence) {
  background-color: rgb(var(--v-theme-calendar-absence)) !important;
}

:deep(.user_has_entry) {
  background-color: rgb(var(--v-theme-calendar-entry)) !important;
}

:deep(tr.calendar-expand-period-row) {
  cursor: pointer;
  opacity: 0.55;
}
:deep(tr.calendar-expand-period-row:hover) {
  opacity: 1;
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

:deep(tr.calendar-expand-period-row > td) {
  height: 26px !important;
  min-height: 0 !important;
  max-height: 26px !important;
  line-height: 26px !important;
}





.canceled-event {
  text-decoration: line-through;
}


</style>




